"use client";

/**
 * Why the submit button is held: staff have paused this program's new videos.
 *
 * It sits wherever a creator would start an upload (Home, the submit page, a
 * request, the videos page) beside a submit button that stays on screen,
 * disabled, rather than vanishing. A button that disappears reads as a
 * portal that broke; one that is held with the reason above it reads as a
 * program that is waiting.
 *
 * `HandlesPrompt`'s row on the warn tone, without the press: there is nothing
 * for the creator to do but wait, so it is not a button. The staff note gets
 * a line of its own at full strength, because it is the one part of this
 * that is news ("Back on Monday").
 */

import { CirclePause } from "lucide-react";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { cn } from "@/lib/utils";
import { CARD_ROW_WARN, Row } from "./one/ui";
import {
  type SubmissionsPause,
  submissionsPausedNote,
} from "./submissionsPaused";

export function SubmissionsPausedNotice({
  brand,
  pause,
  className,
}: {
  /** The program's brand, which may not be the switcher's on a request. */
  brand: string;
  pause: SubmissionsPause;
  /** Placement only. */
  className?: string;
}) {
  const t = useCreatorTranslations("submit.paused");
  const note = submissionsPausedNote(pause);

  return (
    <Row
      className={cn(CARD_ROW_WARN, className)}
      icon={
        <CirclePause
          className="size-4.5 text-nf-ds-tone-warn-fg"
          strokeWidth={2}
        />
      }
      title={t("title")}
      detail={
        <>
          {t("body", { brand })}
          {note ? (
            <span className="mt-nf-ds-tight block whitespace-pre-line text-nf-ds-fg">
              {note}
            </span>
          ) : null}
        </>
      }
    />
  );
}
